import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Headphones, LogOut, Mail, MapPin, Phone, PhoneCall } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { SUPPORT_PHONE, SUPPORT_PHONE_DISPLAY } from '@/lib/domain';
import { formatDate } from '@/lib/format';
import { useAuth, useUserId } from '@/components/AuthProvider';
import { useFeedback } from '@/components/Feedback';
import { ThemeSwitch } from '@/components/ThemeProvider';
import { Avatar, Modal } from '@/components/ui';
import Spinner from '@/components/Spinner';
import styles from './Profile.module.css';

export default function ClientProfile() {
  const userId = useUserId();
  const { session, signOut } = useAuth();
  const { toast, confirm } = useFeedback();
  const [searchParams, setSearchParams] = useSearchParams();

  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showSupport, setShowSupport] = useState(searchParams.get('support') === '1');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');

  useEffect(() => {
    if (!userId) return;
    void (async () => {
      try {
        const { data } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .maybeSingle();

        setProfile(data ?? {});
        setFullName(data?.full_name ?? '');
        setPhone(data?.phone ?? '');
        setCity(data?.city ?? '');
      } catch (err) {
        console.error('Error loading profile:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  const closeSupport = () => {
    setShowSupport(false);
    if (searchParams.has('support')) {
      searchParams.delete('support');
      setSearchParams(searchParams, { replace: true });
    }
  };

  const handleSave = async (event: FormEvent) => {
    event.preventDefault();
    if (!userId) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: fullName.trim(),
          phone: phone.trim(),
          city: city.trim(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', userId);

      if (error) throw new Error(error.message);

      setProfile((current: any) => ({
        ...current,
        full_name: fullName.trim(),
        phone: phone.trim(),
        city: city.trim(),
      }));
      setEditing(false);
      toast('success', 'Profil mis à jour');
    } catch (err) {
      toast('error', 'Impossible de sauvegarder', err instanceof Error ? err.message : undefined);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    const ok = await confirm({
      title: 'Se déconnecter ?',
      body: 'Vous devrez saisir vos identifiants à la prochaine connexion.',
      confirmLabel: 'Déconnexion',
      destructive: true,
    });
    if (ok) await signOut();
  };

  if (loading) return <Spinner block label="Chargement du profil..." />;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <span className={styles.avatar}>
          <Avatar name={profile?.full_name} size={104} tone="orange" />
        </span>
        <h1 className={styles.name}>{profile?.full_name || 'Utilisateur'}</h1>
        <p className={styles.memberSince}>
          Membre depuis {formatDate(profile?.created_at ?? session?.user.created_at)}
        </p>
        <span className={styles.roleBadge}>
          {profile?.account_type === 'provider' ? 'Prestataire' : 'Client'}
        </span>
      </header>

      <div className={styles.body}>
        {editing ? (
          <form className={styles.form} onSubmit={handleSave}>
            <div className="field">
              <label className="label" htmlFor="fullName">
                Nom complet
              </label>
              <input
                id="fullName"
                className="input"
                value={fullName}
                onChange={(event) => setFullName(event.target.value)}
                disabled={saving}
              />
            </div>

            <div className="field">
              <label className="label" htmlFor="phone">
                Téléphone
              </label>
              <input
                id="phone"
                className="input"
                type="tel"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                disabled={saving}
              />
            </div>

            <div className="field">
              <label className="label" htmlFor="city">
                Ville
              </label>
              <input
                id="city"
                className="input"
                value={city}
                onChange={(event) => setCity(event.target.value)}
                disabled={saving}
              />
            </div>

            <div className={styles.formActions}>
              <button
                type="button"
                className="btn btn--outline"
                onClick={() => {
                  setEditing(false);
                  setFullName(profile?.full_name ?? '');
                  setPhone(profile?.phone ?? '');
                  setCity(profile?.city ?? '');
                }}
                disabled={saving}
              >
                Annuler
              </button>
              <button type="submit" className="btn btn--primary" disabled={saving}>
                {saving ? 'Sauvegarde...' : 'Enregistrer'}
              </button>
            </div>
          </form>
        ) : (
          <>
            <ul className={styles.infoList}>
              <li className={styles.infoRow}>
                <span className={styles.infoIcon}>
                  <Mail size={19} />
                </span>
                <div>
                  <p className={styles.infoLabel}>Email</p>
                  <p className={styles.infoValue}>{session?.user.email ?? '—'}</p>
                </div>
              </li>
              <li className={styles.infoRow}>
                <span className={styles.infoIcon}>
                  <Phone size={19} />
                </span>
                <div>
                  <p className={styles.infoLabel}>Téléphone</p>
                  <p className={styles.infoValue}>{profile?.phone || 'Non renseigné'}</p>
                </div>
              </li>
              <li className={styles.infoRow}>
                <span className={styles.infoIcon}>
                  <MapPin size={19} />
                </span>
                <div>
                  <p className={styles.infoLabel}>Ville</p>
                  <p className={styles.infoValue}>{profile?.city || 'Non renseignée'}</p>
                </div>
              </li>
            </ul>

            <section className={styles.appearance}>
              <h2 className={styles.appearanceTitle}>Apparence</h2>
              <ThemeSwitch />
            </section>

            <div className={styles.actions}>
              <button
                type="button"
                className="btn btn--primary btn--block"
                onClick={() => setEditing(true)}
              >
                Modifier le profil
              </button>
              <button
                type="button"
                className="btn btn--outline btn--block"
                onClick={() => setShowSupport(true)}
              >
                <Headphones size={18} /> Service client
              </button>
              <button
                type="button"
                className="btn btn--danger btn--block"
                onClick={() => void handleLogout()}
              >
                <LogOut size={18} /> Déconnexion
              </button>
            </div>
          </>
        )}
      </div>

      <Modal open={showSupport} onClose={closeSupport} title="Service client">
        <img className={styles.supportArt} src="/assets/illustrations/support.png" alt="" />
        <p className={styles.supportText}>Pour toute assistance, contactez-nous au :</p>
        <p className={styles.supportPhone}>{SUPPORT_PHONE_DISPLAY}</p>
        <a className="btn btn--navy btn--block" href={`tel:${SUPPORT_PHONE}`}>
          <PhoneCall size={18} /> Appeler
        </a>
      </Modal>
    </div>
  );
}
